/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { Fragment, FunctionComponent, MouseEvent, useRef } from "react";

import { CONST_LEFT_MOUSE_BTN } from "../constants";
import { AlignType, AnchorPoint, ResizeOptions } from "../enums";
import { IComponent, IPoint } from "../interfaces";
import { LinkPassableProps, MoveablePassableProps, PicassoEditorProps, SelectedAnchorsType, WrapMapStateToProps } from "../types";
import { getAnchorStyle, getDimensionsOfComponent } from "../utils";
import { createLink } from "./Link";

export type MoveableComponentProps = {
    readonly index: number;
    readonly uiComponent: IComponent;
    readonly isSelected: boolean;
    readonly gridTemplate: string;
    readonly rowNumber: number;
    readonly columnNumber: number;
    readonly isSelectable?: boolean;
    readonly isResizable?: boolean;
    readonly isDraggable?: boolean;
    readonly fillsParent?: boolean;
    readonly renderComponent: (componentName: string, passableProps: MoveablePassableProps) => JSX.Element;
    readonly renderLink?: (linkName: string, passableProps: LinkPassableProps) => JSX.Element;
    readonly wrapMapStateToProps: WrapMapStateToProps<any>;
    readonly onResizePointDown: (e: MouseEvent, selectedAnchors: SelectedAnchorsType) => void;
    readonly onMouseUp: (e: MouseEvent, uiComponent: IComponent) => void;
    readonly onMouseClick: (e: MouseEvent, component: IComponent, isSelected: boolean, isSelectable: boolean) => void;
    readonly onMouseLeave: (e: MouseEvent) => void;
    readonly onMouseDown: (e: MouseEvent, isSelected: boolean, isDraggable: boolean, isSelectable: boolean, component: IComponent) => void;
    readonly onMouseMove: (e: MouseEvent, uiComponent: IComponent, isSelected: boolean) => void;
    readonly onLinkChange?: (changedLinkIndexes: number, newPoints: readonly IPoint[]) => void;
} & PicassoEditorProps;

export const MoveableComponent: FunctionComponent<MoveableComponentProps> = ({ picassoInstanceKey, index, uiComponent, isSelected, gridTemplate, rowNumber, columnNumber, renderComponent, renderLink, onMouseUp, onResizePointDown, onMouseDown, onMouseClick, onMouseMove, onMouseLeave, onLinkChange, wrapMapStateToProps, isSelectable = true, isResizable = true, isDraggable = true, fillsParent = false }) => {
    const nAnchor = useRef<HTMLDivElement>(null);
    const neAnchor = useRef<HTMLDivElement>(null);
    const eAnchor = useRef<HTMLDivElement>(null);
    const seAnchor = useRef<HTMLDivElement>(null);
    const sAnchor = useRef<HTMLDivElement>(null);
    const swAnchor = useRef<HTMLDivElement>(null);
    const wAnchor = useRef<HTMLDivElement>(null);
    const nwAnchor = useRef<HTMLDivElement>(null);

    if (!uiComponent.isVisible)
        return null;

    const handleDragOverResizePoint = (e: MouseEvent) => {
        e.stopPropagation();
    };

    const handleMouseUp = (e: MouseEvent) => {
        onMouseUp(e, uiComponent);
    };

    const handleMouseMove = (e: MouseEvent) => {
        onMouseMove(e, uiComponent, isSelected);
    };

    const handleResizePointDown = (e: MouseEvent) => {
        if (e.buttons !== CONST_LEFT_MOUSE_BTN)
            return;

        if (!nAnchor.current || !neAnchor.current || !eAnchor.current || !seAnchor.current || !sAnchor.current || !swAnchor.current || !wAnchor.current || !nwAnchor.current)
            throw new Error("One or more anchor points not found in DOM object!");

        const selectedAnchors: SelectedAnchorsType = {
            nAnchor: { element: nAnchor.current, isSelected: e.currentTarget.id === nAnchor.current.id },
            neAnchor: { element: neAnchor.current, isSelected: e.currentTarget.id === neAnchor.current.id },
            eAnchor: { element: eAnchor.current, isSelected: e.currentTarget.id === eAnchor.current.id },
            seAnchor: { element: seAnchor.current, isSelected: e.currentTarget.id === seAnchor.current.id },
            sAnchor: { element: sAnchor.current, isSelected: e.currentTarget.id === sAnchor.current.id },
            swAnchor: { element: swAnchor.current, isSelected: e.currentTarget.id === swAnchor.current.id },
            wAnchor: { element: wAnchor.current, isSelected: e.currentTarget.id === wAnchor.current.id },
            nwAnchor: { element: nwAnchor.current, isSelected: e.currentTarget.id === nwAnchor.current.id },
        };

        onResizePointDown(e, selectedAnchors);
    };

    const handleComponentClick = (e: MouseEvent) => {
        onMouseClick(e, uiComponent, isSelected, isSelectable);
    };

    const handleComponentMouseDown = (e: MouseEvent) => {
        onMouseDown(e, isSelected, isDraggable && uiComponent.align === AlignType.none, isSelectable, uiComponent);
    };

    const innerComponent = renderComponent(uiComponent.name, {
        uiComponent,
        picassoInstanceKey,
        isSelected,
        gridTemplate,
        renderComponent,
        renderLink,
        onResizePointDown,
        onMouseUp,
        onMouseClick,
        onComponentMouseDown: onMouseDown,
        onMouseMove,
        onMouseLeave,
        clickSelectComponent: handleComponentClick,
        wrapMapStateToProps,
        mouseDownComponentSelect: handleComponentMouseDown,
        onLinkChange,
    });

    const linksSvg = uiComponent.linkIndexes.length > 0 && (
        <svg className="picasso-moveable-svg-box">
            {renderLink != null && uiComponent.linkIndexes.map((index) => {
                const Link = createLink(wrapMapStateToProps);
                return (<Link key={`${index}link`} {...{ index, onLinkChange, picassoInstanceKey, renderLink }} />);
            })}
        </svg>
    );

    const resizePoints = (isResizable && uiComponent.resizeOption !== ResizeOptions.none && isSelected) && (
        <Fragment>
            <div id={index + AnchorPoint.N} ref={nAnchor} style={getAnchorStyle(AnchorPoint.N, uiComponent)} className="picasso-moveable-anchor n" onMouseDown={handleResizePointDown} onDragOver={handleDragOverResizePoint} />
            <div id={index + AnchorPoint.NE} ref={neAnchor} style={getAnchorStyle(AnchorPoint.NE, uiComponent)} className="picasso-moveable-anchor ne" onMouseDown={handleResizePointDown} onDragOver={handleDragOverResizePoint} />
            <div id={index + AnchorPoint.E} ref={eAnchor} style={getAnchorStyle(AnchorPoint.E, uiComponent)} className="picasso-moveable-anchor e" onMouseDown={handleResizePointDown} onDragOver={handleDragOverResizePoint} />
            <div id={index + AnchorPoint.SE} ref={seAnchor} style={getAnchorStyle(AnchorPoint.SE, uiComponent)} className="picasso-moveable-anchor se" onMouseDown={handleResizePointDown} onDragOver={handleDragOverResizePoint} />
            <div id={index + AnchorPoint.S} ref={sAnchor} style={getAnchorStyle(AnchorPoint.S, uiComponent)} className="picasso-moveable-anchor s" onMouseDown={handleResizePointDown} onDragOver={handleDragOverResizePoint} />
            <div id={index + AnchorPoint.SW} ref={swAnchor} style={getAnchorStyle(AnchorPoint.SW, uiComponent)} className="picasso-moveable-anchor sw" onMouseDown={handleResizePointDown} onDragOver={handleDragOverResizePoint} />
            <div id={index + AnchorPoint.W} ref={wAnchor} style={getAnchorStyle(AnchorPoint.W, uiComponent)} className="picasso-moveable-anchor w" onMouseDown={handleResizePointDown} onDragOver={handleDragOverResizePoint} />
            <div id={index + AnchorPoint.NW} ref={nwAnchor} style={getAnchorStyle(AnchorPoint.NW, uiComponent)} className="picasso-moveable-anchor nw" onMouseDown={handleResizePointDown} onDragOver={handleDragOverResizePoint} />
        </Fragment>
    );

    const style = getDimensionsOfComponent(fillsParent, uiComponent, rowNumber, columnNumber);

    return (
        <div id={`${uiComponent.index}_component`} className="picasso-moveable" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} {...{ style, onMouseLeave }}>
            {resizePoints}
            {innerComponent}
            {linksSvg}
        </div>
    );
};
